import { type ReactNode, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setImportingOrExportingState, setOpenSpinnerState } from '../../persisted/global/globalActions';
import { Role } from '../../resource/enumerations/Role';
import { useAdminRights } from '../hooks/useAdminRights';
import { NotFoundComponent } from '../reusable-components/NotFoundComponent';
import { NotPermittedComponent } from '../reusable-components/NotPermittedComponent';
import { useCurrentRoute, useValidateRoute } from './createdRouter';
import type { Path } from './route';
import { useParams } from 'react-router-dom';

const routeToRole: Record<Path, Role | null> = {
    ['/']: null,
    ['/auth/oidc/callback/login']: null,
    ['/admin-panel']: Role.ADMIN,
    ['/admin-panel/system-settings']: Role.ADMIN,
    ['/admin-panel/system-right-management']: Role.ADMIN,
    ['/admin-panel/system-announcements']: Role.ADMIN,
    ['/project-user-management/:id']: Role.MANAGER,
    ['/projects']: null,
    ['/project/:id']: Role.READER,
    ['/project/:id/active-version/:versionId']: Role.READER,
    ['/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId']: Role.READER,

    ['/project/:id/published-use-case/:useCaseObjectId']: Role.READER,
    ['/project/:id/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId']: Role.READER,
    ['/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations']: Role.READER,

    ['/project/:id/published-process-area/:processAreaObjectId']: Role.READER,
    ['/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId']: Role.READER,
    ['/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId']: Role.READER,
    ['/project/:id/active-version/:versionId/process-area/:processAreaId']: Role.READER,
    ['/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId']: Role.READER,
    ['/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId/workflow-representation/:workflowId']: Role.READER,

    ['/project/:id/active-version/:versionId/activities-model/:activitiesId']: Role.PROCESS_ADMIN,
    ['/project/:id/active-version/:versionId/activities-model/:activitiesId/standard/:standardId/use-case/:useCaseId']: Role.PROCESS_ADMIN,
    ['*']: null,
};

export const RouteValidatorComponent = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch();
    const { id: projectId } = useParams<{ id: string }>();

    const currentRoute = useCurrentRoute();

    const { isProcessAdmin, isManager, isAdminOrServerAdmin, isReader } = useAdminRights(projectId);

    useEffect(() => {
        dispatch(setImportingOrExportingState(false));
        dispatch(setOpenSpinnerState(false));
    }, [currentRoute, dispatch]);

    const routeValidity = useValidateRoute();

    const noPermission = useMemo(() => {
        const role = routeToRole[currentRoute];
        return (
            (!isAdminOrServerAdmin && role === Role.ADMIN) ||
            (!isProcessAdmin && role === Role.PROCESS_ADMIN) ||
            (!isManager && role === Role.MANAGER) ||
            (!isReader && role === Role.READER)
        );
    }, [currentRoute, isProcessAdmin, isManager, isAdminOrServerAdmin, isReader]);

    if (routeValidity === 'invalid') {
        return <NotFoundComponent />;
    }

    if (noPermission) {
        return <NotPermittedComponent />;
    }

    return children;
};
